import { Col, Row } from 'antd';
import { withRouter } from 'next/router';
import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import SearchResults from '@/src/compliance/components/CompliAssist/SearchResults';
import {
  requestSearchOptions,
  updateSearchFilters,
} from '@/src/compliance/redux/complianceReducer';
import Form from '@/src/shared/components/Form';
import SearchFilters from './SearchFilters';
import TagTile from './SearchFilters/TagTile';

const FIELD_NAMES = {
  SEARCH: 'searchText',
  STATE: 'states',
  CATEGORY: 'categories',
  CHART: 'charts',
};

const CompliAssistChartSearch = ({
  form,
  searchParams,
  searchFilterKey,
  searchOptions: { categories, charts, states },
  searchFiltersValues,
  updateSearchFilters: updateSearch,
}) => {
  const [filterExpanded, setFilterExpanded] = useState(true);
  const [tagStates, setTagStates] = useState({
    [`tags_${FIELD_NAMES.SEARCH}`]: [],
    [`tags_${FIELD_NAMES.STATE}`]: [],
    [`tags_${FIELD_NAMES.CHART}`]: [],
    [`tags_${FIELD_NAMES.CATEGORY}`]: [],
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const resetTags = useCallback(() => {
    setTagStates({
      [`tags_${FIELD_NAMES.SEARCH}`]: [],
      [`tags_${FIELD_NAMES.STATE}`]: [],
      [`tags_${FIELD_NAMES.CHART}`]: [],
      [`tags_${FIELD_NAMES.CATEGORY}`]: [],
    });
  }, []);

  const getSelectionState = useCallback((value, selectedNodes, input, isInitialVal = false) => {
    const newState = {};
    const tagInput = `tags_${input}`;
    if (isInitialVal) {
      newState[tagInput] = selectedNodes;
    } else if (selectedNodes && Array.isArray(selectedNodes)) {
      const selections = selectedNodes.map(nd => ({ ...nd.props, input }));
      newState[tagInput] = selections;
    } else {
      const selectedProps = selectedNodes && selectedNodes.props;
      const text = selectedProps ? selectedProps.children : value;
      newState[tagInput] = value ? [{ value, input, children: text }] : [];
    }
    return newState;
  }, []);

  const setSelection = useCallback((...args) => {
    const newState = getSelectionState(...args);
    setTagStates(prev => ({ ...prev, ...newState }));
  }, [getSelectionState]);

  const setSelectionTags = useCallback((tags) => {
    const newState = tags.reduce((acc, tag) => {
      const tagState = getSelectionState(...tag);
      Object.assign(acc, tagState);
      return acc;
    }, {});
    setTagStates(prev => ({ ...prev, ...newState }));
  }, [getSelectionState]);

  const handleFiltersCollapseChange = useCallback((activeTabs) => {
    setFilterExpanded(activeTabs.includes('1'));
  }, []);

  const tagClose = useCallback((e, value, input) => {
    e.preventDefault();
    const storedValue = form.getFieldValue(input);
    let selections = '';
    if (Array.isArray(storedValue)) {
      selections = storedValue.filter(v => v !== value);
      setTagStates(state => {
        return {
          ...state,
          [`tags_${input}`]: state[`tags_${input}`].filter(tag => selections.includes(tag.value)),
        };
      });
    } else {
      setTagStates(state => ({ ...state, [`tags_${input}`]: [] }));
    }
    form.setFieldsValue({ [input]: selections });
    updateSearch(input, selections);
  }, [form, updateSearch]);

  const filterTags = [];

  Object.values(FIELD_NAMES).forEach(k => {
    const tagName = `tags_${k}`;
    const tag = tagStates[tagName];
    // Add local state tag
    if (tag && Array.isArray(tag) && tag.length > 0) {
      filterTags.push(...tag);
    } else if (tag && tag.input) {
      filterTags.push(tag);
    }
    // If local state not import yet for searchId query, merge tags with searchFiltersReducer
    const fieldValue = searchFiltersValues[k];
    if (k === FIELD_NAMES['SEARCH'] && fieldValue) {
      const searchState = {
        value: fieldValue,
        input: k,
        children: fieldValue,
      };
      if (!filterTags.find(({ input }) => input === k)) {
        filterTags.push(searchState);
      }
    }
    if (k === FIELD_NAMES['STATE'] && Array.isArray(fieldValue)) {
      fieldValue.forEach(v => {
        const selectedState = states.find(s => s.StateCode === v);
        if (selectedState) {
          const formattedState = {
            value: v,
            children: selectedState.StateDescription,
            input: k,
          };
          if (!filterTags.find(({ input, value }) => input === k && value === v)) {
            filterTags.push(formattedState);
          }
        }
      });
    }
    if (k === FIELD_NAMES['CATEGORY'] && fieldValue) {
      const selectedCategory = categories.find(c => c.CategoryId === fieldValue);
      if (selectedCategory) {
        const formattedCategoryTag = {
          value: fieldValue,
          children: selectedCategory.CategoryDescription,
          input: k,
        };
        if (!filterTags.find(({ input }) => input === k)) {
          filterTags.push(formattedCategoryTag);
        }
      }
    }
    if (k === FIELD_NAMES['CHART'] && Array.isArray(fieldValue)) {
      fieldValue.forEach(v => {
        const selectedChart = charts.find(c => c.ChartId === v);
        if (selectedChart) {
          const formattedChartTag = {
            value: v,
            children: selectedChart.ChartTitle,
            input: k,
          };
          if (!filterTags.find(({ input, value }) => input === k && value === v)) {
            filterTags.push(formattedChartTag);
          }
        }
      });
    }
  });

  return (
    <Row gutter={{ xs: 24, md: 32 }} type="flex">
      <Col xs={24} lg={filterExpanded ? 8 : 24}>
        <SearchFilters
          form={form}
          filterExpanded={filterExpanded}
          setSelection={setSelection}
          setSelectionTags={setSelectionTags}
          searchFilterKey={searchFilterKey}
          handleFiltersCollapseChange={handleFiltersCollapseChange}
          searchParams={searchParams}
          onSaveSearch={() => { }}
          resetTags={resetTags}
          includeDates={false}
          rootSearchURL="/compliance/compliassist"
          isRecentChanges={false}
        />
        {!filterExpanded &&
          filterTags.map(tag => <TagTile key={tag.value} tag={tag} tagClose={tagClose} />)}
      </Col>
      <Col xs={24} lg={filterExpanded ? 16 : 24}>
        {/* DO NOT DELETE CLASSNAME. IT IS NECESSSARY FOR THE COLLAPSE ALL AND EXPAND ALL FUNCTIONS. */}
        <div className="accordion-panels-container">
          <SearchResults displayChanges={false} />
        </div>
      </Col>
    </Row>
  );
};

function mapStateToProps(state) {
  const {
    compliance: { searchOptions, searchFiltersValues },
  } = state;

  return {
    searchOptions,
    searchFiltersValues,
  };
}
export default connect(
  mapStateToProps,
  { requestSearchOptions, updateSearchFilters }
)(Form.create({ name: 'CompliAssistChartSearch' })(withRouter(CompliAssistChartSearch)));
